import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Typography,
  message,
  theme,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SkinOutlined,
  IdcardOutlined,
  FilePdfOutlined,
  MenuOutlined,
  ApartmentOutlined,
  ProfileOutlined,
  TagsOutlined,
  ToolOutlined,
  CrownOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import PageHeader from 'components/headings/PageHeader';
import ColorFieldWithWcag from 'components/admin/tenants/ColorFieldWithWcag';
import { listTenantsAdmin, updateTenantAdmin } from 'api/tenantsAdmin';
import { autoTextColor } from 'utils/colorContrast';
import paths from 'assets/routes/paths';
import useComponentEditRolesApi from 'pages/liftlogic/v2/Components/useComponentEditRolesApi';

const { useToken } = theme;

// ── Preview panel: replica del mockup con los colores en vivo ─────────
function LivePreview({ primary, secondary }) {
  const { token } = useToken();
  const validPrimary = /^#[0-9a-fA-F]{6}$/.test(primary) ? primary : '#0055b3';
  const validSecondary = /^#[0-9a-fA-F]{6}$/.test(secondary) ? secondary : '#c5561d';
  const textOnPrimary = autoTextColor(validPrimary);
  const textOnSecondary = autoTextColor(validSecondary);

  return (
    <Card
      title={
        <Space>
          <SkinOutlined />
          <span>Preview en vivo</span>
        </Space>
      }
      size="small"
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        Así se verán los elementos con estos colores.
      </Typography.Paragraph>

      {/* Mini dashboard */}
      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong style={{ fontSize: 12 }}>
          Dashboard
        </Typography.Text>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 8,
            padding: 12,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: 6,
            background: token.colorBgLayout,
          }}
        >
          <button
            type="button"
            style={{
              background: validPrimary,
              color: textOnPrimary,
              border: 'none',
              padding: '6px 14px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Botón primario
          </button>
          <button
            type="button"
            style={{
              background: validSecondary,
              color: textOnSecondary,
              border: 'none',
              padding: '6px 14px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Botón secundario
          </button>
          <span
            style={{
              border: `1px solid ${validPrimary}`,
              color: validPrimary,
              padding: '2px 8px',
              fontSize: 11,
              borderRadius: 4,
              background: '#fff',
            }}
          >
            Tag
          </span>
          <a
            href="#preview"
            onClick={(e) => e.preventDefault()}
            style={{ color: validPrimary, fontSize: 13, fontWeight: 500 }}
          >
            Ejemplo de link
          </a>
        </div>
        <div
          style={{
            marginTop: 8,
            padding: 12,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: 6,
            background: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 16,
              borderBottom: `2px solid ${validSecondary}`,
              paddingBottom: 4,
              fontSize: 13,
            }}
          >
            <span style={{ fontWeight: 600, color: token.colorText }}>Tab activa</span>
            <span style={{ color: token.colorTextTertiary }}>Tab inactiva</span>
          </div>
          <Input
            placeholder="Input con focus"
            size="small"
            style={{ marginTop: 10, borderColor: validPrimary }}
          />
        </div>
      </div>

      {/* Mini PDF */}
      <div>
        <Typography.Text strong style={{ fontSize: 12 }}>
          Comprobante PDF
        </Typography.Text>
        <div
          style={{
            marginTop: 8,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: 6,
            overflow: 'hidden',
            background: '#fff',
            fontSize: 10,
          }}
        >
          <div
            style={{
              background: validPrimary,
              color: textOnPrimary,
              padding: '8px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            <span>Comprobante de mantención</span>
            <span style={{ fontSize: 9, opacity: 0.85 }}>#12345</span>
          </div>
          <div style={{ padding: '8px 12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
              <thead>
                <tr style={{ background: validPrimary, color: textOnPrimary }}>
                  <th
                    style={{
                      padding: '4px 6px',
                      textAlign: 'left',
                      fontSize: 9,
                      textTransform: 'uppercase',
                    }}
                  >
                    Ítem
                  </th>
                  <th style={{ padding: '4px 6px', textAlign: 'left', fontSize: 9 }}>Cant.</th>
                  <th style={{ padding: '4px 6px', textAlign: 'left', fontSize: 9 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>
                    Cable tracción
                  </td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>1</td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>$48.000</td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>
                    Mano de obra
                  </td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>4h</td>
                  <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>$80.000</td>
                </tr>
              </tbody>
            </table>
            <div
              style={{
                marginTop: 6,
                paddingTop: 4,
                borderTop: `1px solid ${validSecondary}`,
                textAlign: 'right',
                fontWeight: 600,
                fontSize: 10,
              }}
            >
              Total: $128.000
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Página principal ──────────────────────────────────────────────────
function TenantEditPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [tenant, setTenant] = useState(location.state?.tenant || null);
  const [loading, setLoading] = useState(!location.state?.tenant);
  const [saving, setSaving] = useState(false);

  const idNum = Number(tenantId);
  const componentEditRolesApi = useComponentEditRolesApi();
  const [editRoles, setEditRoles] = useState([]);
  const [savingRoles, setSavingRoles] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    componentEditRolesApi
      .get(tenant.id)
      .then((res) => {
        setEditRoles(res.roles || []);
      })
      .catch(() => {
        // silencioso — el default visual vendrá vacío
      });
  }, [tenant, componentEditRolesApi]);

  const handleRolesChange = useCallback(
    async (value) => {
      const prev = editRoles;
      setEditRoles(value);
      setSavingRoles(true);
      try {
        await componentEditRolesApi.set(tenant.id, value);
        message.success('Permisos actualizados');
      } catch {
        setEditRoles(prev);
        message.error('Error al actualizar permisos');
      } finally {
        setSavingRoles(false);
      }
    },
    [componentEditRolesApi, tenant, editRoles]
  );

  useEffect(() => {
    if (tenant) return;
    let cancelled = false;
    listTenantsAdmin()
      .then((rows) => {
        if (cancelled) return;
        const match = rows.find((r) => r.id === idNum);
        if (match) setTenant(match);
        else message.error('Tenant no encontrado');
      })
      .catch(() => {
        if (!cancelled) message.error('Error cargando tenant');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idNum, tenant]);

  useEffect(() => {
    if (!tenant) return;
    form.setFieldsValue({
      organization: tenant.organization,
      correo: tenant.correo,
      telefono: tenant.telefono,
      direccion: tenant.direccion,
      web: tenant.web,
      customDomain: tenant.customDomain,
      primary_color: tenant.primary_color || '#0055b3',
      secondary_color: tenant.secondary_color || '#c5561d',
      plan: tenant.plan || 'lite',
      read_only: tenant.read_only === true || tenant.read_only === 1,
      minvu_enabled: tenant.minvu_enabled === true || tenant.minvu_enabled === 1,
    });
  }, [tenant, form]);

  const onFinish = async (values) => {
    if (!tenant) return;
    setSaving(true);
    try {
      await updateTenantAdmin(tenant.id, values);
      message.success('Tenant actualizado');
      navigate('/admin/tenants');
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  // Suscribe al color primario/secundario del form para el preview.
  const primary = Form.useWatch('primary_color', form);
  const secondary = Form.useWatch('secondary_color', form);

  const collapseItems = useMemo(
    () => [
      {
        key: 'identidad',
        label: (
          <Space>
            <IdcardOutlined />
            <span>Identidad</span>
          </Space>
        ),
        children: (
          <>
            <Form.Item label="Organización" name="organization" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Email" name="correo" rules={[{ type: 'email' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Teléfono" name="telefono">
              <Input />
            </Form.Item>
            <Form.Item label="Dirección" name="direccion">
              <Input />
            </Form.Item>
            <Form.Item label="Web" name="web">
              <Input />
            </Form.Item>
            <Form.Item
              label="Custom Domain"
              name="customDomain"
              extra="Dominio propio del tenant (opcional)."
            >
              <Input placeholder="acme.cl" />
            </Form.Item>
          </>
        ),
      },
      {
        key: 'colores',
        label: (
          <Space>
            <SkinOutlined />
            <span>Colores de marca</span>
          </Space>
        ),
        children: (
          <>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              Se aplican al dashboard y PDFs del tenant. Deben cumplir WCAG AA (contraste ≥ 4.5:1
              con el texto auto-calculado).
            </Typography.Paragraph>
            <ColorFieldWithWcag name="primary_color" label="Color primario" />
            <ColorFieldWithWcag name="secondary_color" label="Color secundario" />
          </>
        ),
      },
      {
        key: 'plan-acceso',
        label: (
          <Space>
            <CrownOutlined />
            <span>Plan y acceso</span>
          </Space>
        ),
        children: (
          <>
            <Form.Item
              label="Plan"
              name="plan"
              extra="Pro habilita presupuestos, colecciones, árbol de componentes y alta de administradores."
            >
              <Select
                style={{ maxWidth: 220 }}
                options={[
                  { value: 'lite', label: 'Lite' },
                  { value: 'pro', label: 'Pro' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Solo lectura (read-only)"
              name="read_only"
              valuePropName="checked"
              extra="El portal del administrador queda en solo-lectura (excepto subir a Carpeta 0)."
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Módulo MINVU"
              name="minvu_enabled"
              valuePropName="checked"
              extra="Habilita certificaciones, alertas regulatorias y semáforo de cumplimiento."
            >
              <Switch />
            </Form.Item>
          </>
        ),
      },
    ],
    []
  );

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div style={{ padding: 16 }}>
        <PageHeader title="Tenant no encontrado" />
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tenants')}>
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title={`Editar tenant: ${tenant.organization}`}
        subtitle={tenant.subdomain ? `${tenant.subdomain}.stiltech.cl` : null}
        actions={
          <Space>
            <Button
              icon={<MenuOutlined />}
              onClick={() =>
                navigate(`/admin/tenants/${tenant.id}/menu-items`, {
                  state: { tenantName: tenant.organization },
                })
              }
            >
              Menú
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() =>
                navigate(`/admin/tenants/${tenant.id}/pdf-templates`, {
                  state: { tenantName: tenant.organization },
                })
              }
            >
              Plantillas PDF
            </Button>
            <Button
              icon={<SafetyCertificateOutlined />}
              onClick={() =>
                navigate(paths.tenantResources.garantias(tenant.id), {
                  state: { tenantName: tenant.organization },
                })
              }
            >
              Garantías
            </Button>
            <Button
              icon={<ApartmentOutlined />}
              onClick={() =>
                navigate(paths.tenantResources.componentTemplates(tenant.id), {
                  state: { tenantName: tenant.organization },
                })
              }
            >
              Plantillas de componentes
            </Button>
            <Button
              icon={<ProfileOutlined />}
              onClick={() =>
                navigate(paths.tenantResources.componentAttributeDefs(tenant.id), {
                  state: { tenantName: tenant.organization },
                })
              }
            >
              Atributos de componentes
            </Button>
            <Button
              icon={<TagsOutlined />}
              onClick={() =>
                navigate(paths.tenantResources.componentTypes(tenant.id), {
                  state: { tenantName: tenant.organization },
                })
              }
            >
              Tipos de componente
            </Button>
            <Button
              icon={<ToolOutlined />}
              onClick={() =>
                navigate(paths.tenantResources.equipos(tenant.id), {
                  state: { tenantName: tenant.organization },
                })
              }
            >
              Equipos (componentes)
            </Button>
            <Select
              mode="multiple"
              allowClear
              loading={savingRoles}
              value={editRoles}
              onChange={handleRolesChange}
              placeholder="Roles que editan componentes"
              style={{ minWidth: 240 }}
              options={[
                { value: 'ejecutivo', label: 'Ejecutivo' },
                { value: 'especialista', label: 'Especialista' },
                { value: 'administrador', label: 'Administrador' },
              ]}
            />
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tenants')}>
              Volver
            </Button>
          </Space>
        }
      />

      <Form layout="vertical" form={form} onFinish={onFinish}>
        <div
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Collapse
              defaultActiveKey={['identidad', 'colores', 'plan-acceso']}
              items={collapseItems}
            />
            <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
              <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                Guardar cambios
              </Button>
              <Button onClick={() => navigate('/admin/tenants')}>Cancelar</Button>
            </div>
          </div>

          <div style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
            <LivePreview primary={primary} secondary={secondary} />
          </div>
        </div>
      </Form>
    </div>
  );
}

export default TenantEditPage;
